"use client";

import Image from "next/image";
import Link from "next/link";
import { useState, useRef } from "react";
import { Check, ChevronRight } from "lucide-react";

import type { BlockProps, StrapiBlock } from "@/app/components/blocks/types";
import PreCommandePopup from "./CommandePopup";

type Media = {
  id?: number;
  documentId?: string;
  url?: string | null;
  alternativeText?: string | null;
};

type MenuCategorie = {
  id?: number;
  categorie?: string | null;
  media?: Media[];
  prixTTC?: string | number | null;
  prixHT?: string | number | null;
  prixFiscales?: string | number | null;
};

type MenuMarcheBlock = StrapiBlock & {
  __component: "blocks.menu-marche";
  titre?: string | null;
  description?: string | null;
  categorie?: MenuCategorie[];
};

function formatPrice(value?: string | number | null) {
  if (value === null || value === undefined || value === "") return "";

  if (typeof value === "number") {
    return `${value.toFixed(2)} €`;
  }

  return value.includes("€") ? value : `${value} €`;
}


export default function MenuMarche({
  data,
}: BlockProps<MenuMarcheBlock>) {
  const categories = data.categorie || [];

  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const tabsRef = useRef<HTMLDivElement | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  const [showPreCommandePopup, setShowPreCommandePopup] = useState(false);

  const [scrollProgress, setScrollProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  const handleTabsScroll = () => {
    const el = tabsRef.current;
    if (!el) return;

    const maxScroll = el.scrollWidth - el.clientWidth;

    setScrollProgress(
      maxScroll > 0 ? el.scrollLeft / maxScroll : 0
    );
  };

  const moveSlider = (clientX: number) => {
    const tabs = tabsRef.current;
    const track = trackRef.current;

    if (!tabs || !track) return;

    const rect = track.getBoundingClientRect();

    const visibleRatio = Math.min(
      tabs.clientWidth / tabs.scrollWidth,
      1
    );

    const thumbWidth = rect.width * visibleRatio;
    const maxLeft = rect.width - thumbWidth;

    let left = clientX - rect.left - thumbWidth / 2;

    left = Math.max(0, Math.min(left, maxLeft));

    const progress =
      maxLeft > 0 ? left / maxLeft : 0;

    const maxScroll =
      tabs.scrollWidth - tabs.clientWidth;

    tabs.scrollTo({
      left: progress * maxScroll,
      behavior: "auto",
    });

    setScrollProgress(progress);
  };
  const activeCategory = categories[activeCategoryIndex];

  

  const images = activeCategory?.media || [];
  const activeImage = images[activeImageIndex] || images[0];

  const handleCategoryChange = (index: number) => {
    setActiveCategoryIndex(index);
    setActiveImageIndex(0);
  };

  if (categories.length === 0) return null;

  return (
    <> <section className="bg-muted py-6 ">
      <div className="lms-container">
        <div className="mx-auto max-w-[1100px]">

          {/* TITRE */}
          <div className="mb-5 text-center">
            {data.titre && (
              <h2 className="mb-2 text-4xl lg:text-[44px] font-playfair font-bold text-textColor ">
                {data.titre}
              </h2>
            )}

            {data.description && (
              <p className="mx-auto max-w-[780px] text-base text-textColor lg:text-lg">
                {data.description}
              </p>
            )}
          </div>

          {/* ONGLETS */}
          <div className="mb-5 lg:mb-2 2xl:mb-10">

            {/* SWIPER */}
            <div
              ref={tabsRef}
              onScroll={handleTabsScroll}
              className="w-full snap-x snap-mandatory overflow-x-auto scroll-smooth pb-2 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              <div className="flex w-max gap-2 rounded-xl bg-white p-2 lg:mx-auto lg:gap-3">

                {categories.map((category, index) => {
                  const active = index === activeCategoryIndex;

                  return (
                    <button
                      key={category.id ?? index}
                      type="button"
                      onClick={() => handleCategoryChange(index)}
                      className={`min-w-[105px] shrink-0 snap-start rounded-lg px-4 py-3 text-base font-semibold transition-colors sm:min-w-[120px] lg:min-w-[125px] lg:px-6 lg:text-lg ${active
                        ? "bg-primaryGreen text-white"
                        : "bg-[#F5F5F5] text-textColor"
                        }`}
                    >
                      {category.categorie}
                    </button>
                  );
                })}

              </div>
            </div>

            {/* BARRE DE SWIPE MOBILE */}
            <div className="mx-auto mt-2 h-[3px] w-full overflow-hidden rounded-full bg-gray-200 lg:hidden">
              <div
                className="h-full w-[35%] rounded-full bg-primaryGreen transition-transform duration-200"
                style={{
                  transform: `translateX(${scrollProgress * 185}%)`,
                }}
              />
            </div>

          </div>

          {/* CONTENU */}
          <div className="grid items-center gap-10 lg:grid-cols-[120px_440px_390px]">

            {/* MINIATURES */}
            <div className="flex gap-2 overflow-x-auto lg:flex-col lg:overflow-visible">
              {images.map((image, index) => {
                const selected = index === activeImageIndex;

                return (
                  <button
                    key={image.documentId ?? image.id ?? index}
                    type="button"
                    onClick={() => setActiveImageIndex(index)}
                    className={`relative h-[105px] w-[90px] shrink-0 overflow-hidden rounded-2xl border-[3px] 2xl:h-[145px] 2xl:w-[120px] ${selected
                      ? "border-primaryGreen"
                      : "border-transparent"
                      }`}
                  >
                    {image.url && (
                      <Image
                        src={image.url}
                        alt={image.alternativeText || ""}
                        fill
                        className="object-cover"
                      />
                    )}
                  </button>
                );
              })}
            </div>

            {/* IMAGE PRINCIPALE */}
            <div className="relative h-[380px] w-full overflow-hidden rounded-2xl lg:h-[400px] xl:h-[430px] 2xl:h-auto 2xl:aspect-[4/5]">
              {activeImage?.url && (
                <Image
                  src={activeImage.url}
                  alt={activeImage.alternativeText || ""}
                  fill
                  priority
                  className="object-cover"
                />
              )}
            </div>

            {/* INFORMATIONS */}
            <div className="flex h-full flex-col justify-center">

              {/* PRIX */}
              <div>
                {activeCategory?.prixTTC && (
                  
                  <p className="text-4xl font-bold text-primaryGreen font-playfair lg:text-[34px]">
                    {formatPrice(activeCategory.prixTTC)} TTC

                  </p>
                )}

                {activeCategory?.prixHT && (
                  <p className="mt-2 text-lg font-normal text-gray-500">
                    ({formatPrice(activeCategory.prixHT)} HT)
                  </p>
                )}

                {activeCategory?.prixFiscales && (
                  <p className="mt-1 text-2xl font-medium text-gray-500">
                    {formatPrice(activeCategory.prixFiscales)} TTC après aides fiscales *
                  </p>
                )}
              </div>

              {/* PETIT TRAIT VERT */}
              <div className="my-1 h-1 w-20 rounded-full bg-gradient-to-r from-primaryGreen to-transparent 2xl:my-3" />
              {/* COMPOSITION STATIQUE */}
              <div className="max-w-[390px]">
                {[
                  "Une entrée fraîche et savoureuse",
                  "Un plat principal et sa garniture",
                  "Un laitage",
                ].map((item) => (
                  <div
                    key={item}
                    className="flex items-center gap-3 border-b border-gray-400 py-2"
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-greenPastel">
                      <Check
                        size={17}
                        strokeWidth={3}
                        className="text-primaryGreen"
                      />
                    </span>

                    <p className="text-lg font-semibold text-textColor lg:text-xl">
                      {item}
                    </p>
                  </div>
                ))}

                <p className="mt-2 font-serif text-2xl italic text-primaryGreen">
                  Ou
                </p>

                <p className="mt-2 text-lg font-semibold text-textColor lg:text-xl">
                  un dessert au choix
                </p>
              </div>

              {/* CTA */}
              <div className="mt-2">
                <button
                  type="button"
                  onClick={() => setShowPreCommandePopup(true)}
                  className="min-w-[185px] rounded-lg bg-secondary px-8 py-4 font-semibold uppercase text-white transition-opacity hover:opacity-90"
                >
                  Commander
                </button>
              </div>

            </div>

          </div>
        </div>
      </div>
    </section>
      {
        showPreCommandePopup && (
          <PreCommandePopup
            onClose={() => setShowPreCommandePopup(false)}
            parcoursHref="/commander"
          />
        )
      }
    </>

  );


}
